/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// Vanda Liamo related products moving scrollbar - smooth fix 2026-05-13
(function () {
  function updateRelatedScrollbar(root) {
    const section = root || document;

    section.querySelectorAll('product-recommendations[intent="related"]').forEach(function (recommendations) {
      const wrap = recommendations.querySelector('.scroller');
      const inner = recommendations.querySelector('.product-list__inner--scroller');
      const bar = recommendations.querySelector('.vl-related-products-scrollbar');
      const thumb = recommendations.querySelector('.vl-related-products-scrollbar__thumb');
      if (!wrap || !inner || !bar || !thumb) return;

      const scroller = wrap.scrollWidth > wrap.clientWidth ? wrap : inner;
      let ticking = false;

      function render() {
        ticking = false;

        const maxScroll = Math.max(scroller.scrollWidth - scroller.clientWidth, 0);
        if (maxScroll <= 3) {
          bar.style.display = 'none';
          return;
        }

        bar.style.display = 'block';
        const ratio = scroller.clientWidth / scroller.scrollWidth;
        const thumbWidth = Math.max(54, Math.round(bar.clientWidth * ratio));
        const travel = Math.max(bar.clientWidth - thumbWidth, 0);
        const left = (scroller.scrollLeft / maxScroll) * travel;

        thumb.style.width = thumbWidth + 'px';
        thumb.style.transform = 'translate3d(' + left + 'px,0,0)';
      }

      function requestRender() {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(render);
      }

      if (scroller.dataset.vlMovingScrollbarReady !== 'smooth') {
        scroller.dataset.vlMovingScrollbarReady = 'smooth';
        scroller.addEventListener('scroll', requestRender, { passive: true });
        window.addEventListener('resize', requestRender);
      }

      requestRender();
      setTimeout(requestRender, 250);
      setTimeout(requestRender, 900);
    });
  }

  document.addEventListener('DOMContentLoaded', function () { updateRelatedScrollbar(document); });
  document.addEventListener('shopify:section:load', function (event) { updateRelatedScrollbar(event.target); });

  const observer = new MutationObserver(function () { updateRelatedScrollbar(document); });
  observer.observe(document.documentElement, { childList: true, subtree: true });

  updateRelatedScrollbar(document);
})();

/* VANDA_HIDE_STICKY_ATC_WHEN_CART_DRAWER_OPEN_JS_v1 */
(function(){
  function sync(){
    var cart = document.getElementById('mini-cart') || document.querySelector('cart-drawer[open]');
    var open = !!(cart && cart.hasAttribute('open'));
    document.documentElement.classList.toggle('vd-cart-drawer-open', open);
    if (document.body) document.body.classList.toggle('vd-cart-drawer-open', open);
  }
  document.addEventListener('DOMContentLoaded', function(){
    sync();
    var cart = document.getElementById('mini-cart') || document.querySelector('cart-drawer');
    if (cart) new MutationObserver(sync).observe(cart, { attributes: true, attributeFilter: ['open'] });
  });
  ['cart:refresh','cart:updated','cart:change','cart-drawer:open'].forEach(function(ev){ document.addEventListener(ev, function(){ setTimeout(sync, 0); }); });
  document.addEventListener('click', function(){ setTimeout(sync, 0); }, true);
})();
/* /VANDA_HIDE_STICKY_ATC_WHEN_CART_DRAWER_OPEN_JS_v1 */

/* revoq-eu-withdrawal-hide-branding-v1 */
(function(){
  if (!/\/pages\/eu-withdrawal-form\/?$/.test(window.location.pathname)) return;
  function hideBranding(root){
    if (!root) return;
    root.querySelectorAll('.widerruf-form-branding').forEach(function(el){
      el.style.setProperty('display','none','important');
      el.setAttribute('aria-hidden','true');
    });
    root.querySelectorAll('a,div,span,p,footer').forEach(function(el){
      var txt=(el.textContent||'').replace(/\s+/g,' ').trim();
      var isLeafBranding = /Powered by Revoq|EU Withdrawal Button|Revoq-Logo/i.test(txt) && el.querySelectorAll('input,button,form,[data-widerruf-step]').length === 0 && txt.length < 120;
      if (isLeafBranding) {
        el.style.setProperty('display','none','important');
        el.setAttribute('aria-hidden','true');
      }
    });
  }
  function run(){
    var host = document.querySelector('[data-widerruf-inline]');
    var root = host && host.shadowRoot;
    if (!root) return false;
    hideBranding(root);
    if (!root.__revoqBrandingHideObserver) {
      root.__revoqBrandingHideObserver = new MutationObserver(function(){
        window.clearTimeout(root.__revoqBrandingHideTimer);
        root.__revoqBrandingHideTimer = window.setTimeout(function(){ hideBranding(root); }, 50);
      });
      root.__revoqBrandingHideObserver.observe(root, {childList:true, subtree:true});
    }
    return true;
  }
  var tries = 0;
  var timer = setInterval(function(){ if (run() || ++tries > 60) clearInterval(timer); }, 250);
  document.addEventListener('DOMContentLoaded', run);
})();
/* /revoq-eu-withdrawal-hide-branding-v1 */
